<template>
  <div class="countdown-container">
    <div class="countdown-header text-center mb-4">
      <h3 class="countdown-title text-gradient-primary">
        <SvgIcon name="clock" size="lg" color="primary" class="me-2" />
        Summit Countdown
      </h3>
      <p class="countdown-subtitle">Don't miss the most anticipated digital workplace event in Africa</p>
    </div>

    <div class="countdown-grid">
      <div
        v-for="(unit, index) in timeUnits"
        :key="unit.label"
        class="countdown-unit card-glass hover-lift"
        :class="`countdown-unit-${index}`"
      >
        <div class="countdown-flip-container">
          <div class="countdown-number-display">
            <div class="countdown-number" :class="{ 'flip-animation': unit.isFlipping }">
              {{ unit.value.toString().padStart(2, '0') }}
            </div>
          </div>
          <div class="countdown-separator"></div>
        </div>
        <div class="countdown-label">{{ unit.label }}</div>
        <div class="countdown-progress">
          <div class="progress-ring" :style="{ '--progress': unit.progress }">
            <svg viewBox="0 0 40 40">
              <circle cx="20" cy="20" r="16" stroke="rgba(255, 255, 255, 0.1)" stroke-width="2" fill="none"/>
              <circle
                cx="20"
                cy="20"
                r="16"
                :stroke="unit.color"
                stroke-width="2"
                fill="none"
                stroke-linecap="round"
                :stroke-dasharray="`${unit.progress * 100.53} 100.53`"
                transform="rotate(-90 20 20)"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Status Badge -->
    <div class="event-status-container text-center mt-4">
      <div v-if="!isEventStarted && !isEventEnded" class="event-status-badge bg-gradient-primary-full">
        <SvgIcon name="calendar" size="md" color="white" class="me-2" />
        <span class="fw-bold">{{ formatEventStatus() }}</span>
      </div>
      <div v-else-if="isEventStarted && !isEventEnded" class="event-status-badge bg-gradient-accent-full">
        <SvgIcon name="play" size="md" color="white" class="me-2" />
        <span class="fw-bold">Event is Live Now!</span>
      </div>
      <div v-else class="event-status-badge bg-gradient-secondary-full">
        <SvgIcon name="check-circle" size="md" color="white" class="me-2" />
        <span class="fw-bold">Event Completed</span>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="countdown-actions text-center mt-4">
      <button
        v-if="!isEventEnded"
        class="btn btn-gradient-primary me-3 hover-glow"
        @click="scrollToRegistration"
      >
        <SvgIcon name="users" size="sm" color="white" class="me-2" />
        Register Now
      </button>
      <button
        class="btn btn-outline-primary hover-lift"
        @click="scrollToAgenda"
      >
        <SvgIcon name="calendar" size="sm" color="primary" class="me-2" />
        View Agenda
      </button>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { animate } from 'animejs'
import SvgIcon from './SvgIcon.vue'

export default {
  name: 'CountdownTimer',
  components: {
    SvgIcon
  },
  props: {
    targetDate: {
      type: String,
      default: '2026-02-25T08:00:00Z' // February 25, 2026 8:00 AM UTC (Zanzibar timezone)
    },
    endDate: {
      type: String,
      default: '2026-02-27T18:00:00Z' // February 27, 2026 6:00 PM UTC (Zanzibar timezone)
    }
  },
  setup(props) {
    const timeUnits = ref([
      {
        label: 'Days',
        value: 0,
        maxValue: 365,
        progress: 0,
        isFlipping: false,
        color: 'var(--idws-royal-blue)'
      },
      {
        label: 'Hours',
        value: 0,
        maxValue: 24,
        progress: 0,
        isFlipping: false,
        color: 'var(--idws-bright-blue)'
      },
      {
        label: 'Minutes',
        value: 0,
        maxValue: 60,
        progress: 0,
        isFlipping: false,
        color: 'var(--idws-vibrant-green)'
      },
      {
        label: 'Seconds',
        value: 0,
        maxValue: 60,
        progress: 0,
        isFlipping: false,
        color: 'var(--idws-golden-yellow)'
      }
    ])

    const isEventStarted = ref(false)
    const isEventEnded = ref(false)
    const interval = ref(null)

    const calculateTimeRemaining = () => {
      const now = new Date().getTime()
      const targetTime = new Date(props.targetDate).getTime()
      const endTime = new Date(props.endDate).getTime()

      // Check event status
      if (now >= endTime) {
        isEventEnded.value = true
        isEventStarted.value = false
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      } else if (now >= targetTime) {
        isEventStarted.value = true
        isEventEnded.value = false
        // Show time until event ends
        const distance = endTime - now
        return calculateUnits(distance)
      } else {
        isEventStarted.value = false
        isEventEnded.value = false
        // Show time until event starts
        const distance = targetTime - now
        return calculateUnits(distance)
      }
    }

    const calculateUnits = (distance) => {
      if (distance <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 }
      }

      return {
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((distance % (1000 * 60)) / 1000)
      }
    }

    const updateCountdown = () => {
      const timeRemaining = calculateTimeRemaining()
      const newValues = [
        timeRemaining.days,
        timeRemaining.hours,
        timeRemaining.minutes,
        timeRemaining.seconds
      ]

      newValues.forEach((newValue, index) => {
        const unit = timeUnits.value[index]
        const oldValue = unit.value

        if (oldValue !== newValue) {
          // Update value immediately without animation
          unit.value = newValue
          unit.isFlipping = false

          // Add a subtle scale effect for visual feedback
          animate(`.countdown-unit-${index} .countdown-number`, {
            scale: [1, 1.1, 1],
            duration: 300,
            easing: 'easeOutQuad'
          })
        }

        // Update progress for the circular indicator
        if (index === 0) { // Days
          unit.progress = 1 - (newValue / 365) // Assuming max 1 year countdown
        } else {
          unit.progress = (unit.maxValue - newValue) / unit.maxValue
        }
      })
    }

    const formatEventStatus = () => {
      if (isEventStarted.value) {
        return 'Event in Progress'
      } else if (isEventEnded.value) {
        return 'Event Completed'
      } else {
        const timeRemaining = calculateTimeRemaining()
        if (timeRemaining.days > 0) {
          return `Starting in ${timeRemaining.days} days`
        } else if (timeRemaining.hours > 0) {
          return `Starting in ${timeRemaining.hours} hours`
        } else {
          return 'Starting Very Soon!'
        }
      }
    }

    const scrollToRegistration = () => {
      const element = document.getElementById('registration-section')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        // Navigate to registration page if section doesn't exist
        window.location.href = '/registration'
      }
    }

    const scrollToAgenda = () => {
      const element = document.getElementById('agenda-section')
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      } else {
        // Navigate to agenda page if section doesn't exist
        window.location.href = '/agenda'
      }
    }

    onMounted(() => {
      // Set initial countdown values without animation
      const timeRemaining = calculateTimeRemaining()
      const initialValues = [
        timeRemaining.days,
        timeRemaining.hours,
        timeRemaining.minutes,
        timeRemaining.seconds
      ]

      initialValues.forEach((value, index) => {
        const unit = timeUnits.value[index]
        unit.value = value

        // Calculate initial progress
        if (index === 0) { // Days
          unit.progress = 1 - (value / 365)
        } else {
          unit.progress = (unit.maxValue - value) / unit.maxValue
        }
      })

      // Set up interval for updates
      interval.value = setInterval(updateCountdown, 1000)

      // Animate countdown units on mount with stagger
      animate('.countdown-unit', {
        opacity: [0, 1],
        scale: [0.8, 1],
        translateY: [30, 0],
        duration: 800,
        delay: (el, i) => i * 100,
        easing: 'easeOutExpo'
      })

      // Ensure numbers are upright initially
      animate('.countdown-number', {
        rotateX: 0,
        duration: 0
      })
    })

    onUnmounted(() => {
      if (interval.value) {
        clearInterval(interval.value)
      }
    })

    return {
      timeUnits,
      isEventStarted,
      isEventEnded,
      formatEventStatus,
      scrollToRegistration,
      scrollToAgenda
    }
  }
}
</script>

<style scoped>
.countdown-container {
  max-width: 600px;
  margin: 0 auto;
  padding: var(--spacing-2xl);
}

.countdown-header {
  margin-bottom: var(--spacing-xl);
}

.countdown-title {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  justify-content: center;
}

.countdown-subtitle {
  font-size: var(--font-size-lg);
  color: var(--idws-text-muted);
  font-weight: 400;
  margin-bottom: 0;
}

.countdown-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.countdown-unit {
  position: relative;
  text-align: center;
  padding: var(--spacing-xl) var(--spacing-lg);
  border-radius: 20px;
  transition: all 0.3s ease;
  opacity: 0; /* For animation on mount */
  background: var(--glass-background);
  backdrop-filter: blur(16px);
  border: 1px solid var(--glass-border);
}

.countdown-unit:hover {
  transform: translateY(-8px) scale(1.05);
  box-shadow: var(--shadow-xl);
}

.countdown-flip-container {
  position: relative;
  margin-bottom: var(--spacing-md);
}

.countdown-number-display {
  position: relative;
  perspective: 1000px;
}

.countdown-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--idws-royal-blue);
  font-family: var(--font-primary);
  line-height: 1;
  transition: all 0.3s ease;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
  transform: rotateX(0deg);
  transform-origin: center;
  backface-visibility: hidden;
}

.countdown-number.flip-animation {
  transform-style: preserve-3d;
}

.countdown-separator {
  height: 2px;
  background: var(--idws-gradient-primary);
  border-radius: 2px;
  margin: var(--spacing-sm) auto var(--spacing-md);
  width: 40px;
  opacity: 0.6;
}

.countdown-label {
  font-size: var(--font-size-base);
  color: var(--idws-text-muted);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: var(--spacing-sm);
}

.countdown-progress {
  display: flex;
  justify-content: center;
  margin-top: var(--spacing-md);
}

.progress-ring {
  width: 40px;
  height: 40px;
}

.progress-ring svg {
  width: 100%;
  height: 100%;
  transition: all 0.5s ease;
}

.event-status-container {
  margin: var(--spacing-xl) 0;
}

.event-status-badge {
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-md) var(--spacing-xl);
  border-radius: 25px;
  color: white;
  font-size: var(--font-size-base);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: var(--shadow-md);
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    box-shadow: var(--shadow-md);
  }
  50% {
    transform: scale(1.05);
    box-shadow: var(--shadow-lg);
  }
}

.countdown-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--spacing-md);
  flex-wrap: wrap;
}

.btn-outline-primary {
  border: 2px solid var(--idws-bright-blue);
  color: var(--idws-bright-blue);
  background: transparent;
  border-radius: 12px;
  padding: var(--spacing-md) var(--spacing-xl);
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-outline-primary:hover {
  background: var(--idws-bright-blue);
  color: white;
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

/* Responsive Design */
@media (max-width: 768px) {
  .countdown-container {
    padding: var(--spacing-lg);
  }

  .countdown-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-md);
  }

  .countdown-unit {
    padding: var(--spacing-lg) var(--spacing-md);
  }

  .countdown-number {
    font-size: 2rem;
  }

  .countdown-title {
    font-size: var(--font-size-2xl);
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  .countdown-subtitle {
    font-size: var(--font-size-base);
  }

  .countdown-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .countdown-actions .btn {
    margin: var(--spacing-xs) 0;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .countdown-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--spacing-sm);
  }

  .countdown-unit {
    padding: var(--spacing-md) var(--spacing-sm);
  }

  .countdown-number {
    font-size: 1.5rem;
  }

  .countdown-label {
    font-size: var(--font-size-sm);
  }

  .event-status-badge {
    padding: var(--spacing-sm) var(--spacing-lg);
    font-size: var(--font-size-sm);
  }
}

/* Special effects for very close to event */
.countdown-unit.urgent {
  animation: glow 1.5s ease-in-out infinite alternate;
}

@keyframes glow {
  from {
    box-shadow: var(--shadow-md);
  }
  to {
    box-shadow: 0 0 20px rgba(251, 191, 36, 0.5);
  }
}
</style>